<template>
    <div
        class="workflow-task-alert clickable"
        :class="[
            compact ? 'workflow-task-alert--one-column' : 'workflow-task-alert--two-column',
            `workflow-task-alert--${priority.toLowerCase()}`,
        ]"
        @click="alertClicked(subCampaignId)"
    >
        <div class="workflow-task-alert__body">
            <div class="workflow-task-alert__header">
                <div class="workflow-task-alert__header--name">{{ subCampaignName }}</div>
                <div
                    class="workflow-task-alert__header--tag"
                    :class="`workflow-task-alert__header--${priority.toLowerCase()}`"
                >
                    {{
                        getTag({
                            deadlineDateTime: deadlineDateTime,
                            dueDateTime: dueDateTime,
                        })
                    }}
                </div>
            </div>
            <div class="workflow-task-alert__hierarchyGroup">
                {{ getHierarchyGroup(hierarchyGroup) }}
            </div>
            <div class="workflow-task-alert__priority">{{ priority }}</div>
            <div
                class="workflow-task-alert__message"
                :class="`workflow-task-alert__message--${priority.toLowerCase()}`"
            >
                {{
                    getAlertMessage({
                        alertMessageKey,
                        entity,
                        task,
                        deadlineDateTime,
                    })
                }}
            </div>
        </div>
        <div
            v-if="!compact"
            class="workflow-task-alert__arrow"
            :class="`workflow-task-alert__arrow--${priority.toLowerCase()}`"
        >
            <icon icon-name="mdi-chevron-right" />
        </div>
    </div>
</template>

<script>
import navigation from '@/js/navigation';

export default {
    props: {
        subCampaignName: {
            type: String,
            required: true,
        },
        subCampaignId: {
            type: String,
            required: true,
        },
        alertMessageKey: {
            type: String,
            required: true,
        },
        priority: {
            type: String,
            required: true,
        },
        hierarchyGroup: {
            type: String,
        },
        task: {
            type: String,
            required: true,
        },
        entity: {
            type: String,
            required: true,
        },
        deadlineDateTime: {
            type: String,
            required: true,
        },
        dueDateTime: {
            type: String,
            required: true,
        },
        compact: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['sidebar-closed'],
    methods: {
        getAlertMessage({ alertMessageKey, entity, task, deadlineDateTime }) {
            const today = this.$moment();
            const relativeDueDate = today.to(this.$moment(deadlineDateTime));
            const absoluteDate = this.$moment(deadlineDateTime).format('L');

            const alertType = this.$t(`workflow.taskAlertList.alertTypes.${entity}.${task}`);

            return this.toSentenceCase(
                this.$t(`workflow.taskAlertList.messages.${alertMessageKey}`, {
                    alertType,
                    relativeDueDate,
                    absoluteDate,
                })
            );
        },
        getHierarchyGroup(hierarchyGroup) {
            if (hierarchyGroup) {
                return this.toSentenceCase(this.$t(`workflow.hierarchyGroup.${hierarchyGroup}`));
            }
        },
        alertClicked(subCampaignId) {
            navigation.toSubCampaignView({
                subCampaignId,
                router: this.$router,
            });
            this.$emit('sidebar-closed');
        },
        getTag({ deadlineDateTime, dueDateTime }) {
            const today = this.$moment();
            let tag = null;
            if (today.isAfter(dueDateTime)) {
                tag = this.$t(`workflow.taskAlertList.due`);
            }

            if (today.isAfter(deadlineDateTime)) {
                tag = this.$t(`workflow.taskAlertList.overdue`);
            }

            return tag;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.workflow-task-alert {
    border: 0.1rem solid;
    box-sizing: border-box;
    border-radius: 0.3rem;
    margin-bottom: 0.5rem;
    background-color: $promo-white;
    display: grid;
    grid-auto-flow: column;
    @include alerts-colours(border-color);

    &--one-column {
        grid-template-columns: auto;
    }

    &--two-column {
        grid-template-columns: auto 2.5rem;
    }

    &__body {
        display: grid;
        padding: 0.5rem;
    }

    &__arrow {
        display: flex;
        align-items: center;
        color: $promo-white;
        @include alerts-colours(background-color);

        :deep(.v-icon.v-icon) {
            font-size: 2.4rem !important;
        }
    }

    &__header {
        display: grid;
        grid-template-columns: auto 4.5rem;
        font-size: 1.2rem;
        font-weight: bold;

        &--tag {
            text-align: center;
        }
        @include alerts-colours(color);
    }
    &__message {
        font-size: 1.2rem;
        @include alerts-colours(color);
    }
}
</style>
